import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import AddButton from 'components/AddButton';
import DeleteActionButton from 'components/DeleteActionButton';
import EditActionButton from 'components/EditActionButton';
import CheckBox from 'components/final-form/CheckBox';
import { DropdownOption, stringArrayToDropdownOptionArray } from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import Select from 'components/final-form/Select';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import PxGrid from 'components/layout/PxGrid';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import LoaderComponent from 'components/LoaderComponent';
import SpecialCaseProductModal from 'components/product/SpecialCaseProductModal';
import UpsertProductForm from 'components/product/UpsertProductForm';
import SearchResultComponent from 'components/SearchResultComponent';
import { FormState } from 'final-form';
import _ from 'lodash';
import React, { Component } from "react";
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { RouteComponentProps } from 'react-router';
import { Grid, GridColumn, GridRow, Icon, Modal } from 'semantic-ui-react';
import { getAllArticleGroups } from 'services/articleGroupServices';
import { getResources } from 'services/patientCentralServices';
import { deleteProduct, findProductById, searchProducts } from 'services/productServices';
import { getAllTariffs } from 'services/tariffServices';
import styled from 'styled-components';
import { debounce } from 'ts-debounce';
import { InterventionExternalCode } from 'ts-types/api.enums';
import {
  ArticleGroupDto,
  ProductDto,
  ProductItem,
  ProductSearchRequest,
  ResourceDto,
  TariffDto,
  UpsertProductDto
} from "ts-types/api.types";
import { isoToDisplayDate } from 'utils/dateUtils';
import { noop } from 'utils/functionUtils';
import { withRouterWorkaround } from 'utils/workaroundUtils';

const StyledTable = styled(VirtualizedTable)`
  .ReactVirtualized__Table__rowColumn {
    white-space: break-spaces;
  }

  .inactive-row, .inactive-row:HOVER {
    color: #808080 !important;
    background: #e5e5e5 !important;
  }
`;

const initialProductsSearch: Partial<ProductSearchRequest> = {
  includeInactive: false,
  selfServiceOnly: false
};

interface ProductFieldProps extends FormState {
  values: Partial<ProductSearchRequest>
}

export enum InterventionAvailabilityType {
  VACCINATION,
  OTHER
}

interface Props extends MessageSourceProps, AuthConsumerRenderProps, RouteComponentProps<any> {

}

interface State {
  pageDataLoaded: boolean;
  interventionAvailabilityType: InterventionAvailabilityType,

  resources: ResourceDto[];
  resourceMap: { [id: number]: ResourceDto };
  resourcesOptions: DropdownOption[];

  articleGroupOptions: DropdownOption[];

  interventionTypeOptions: DropdownOption[],
  foundProducts: ProductDto[],
  tariffMap: { [id: number]: TariffDto },
  productsLoaded: boolean,
  addEditModalOpen: boolean,
  productId?: string,
  product?: UpsertProductDto,
  specialProductsModalOpen: boolean,
  searchValues: Partial<ProductSearchRequest>,
  cancelTokenSource: CancelTokenSource
}

class ProductView extends Component<Props, State> {

  initialSearchValues: Partial<ProductSearchRequest> = {};
  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    const interventionTypeOptions = stringArrayToDropdownOptionArray(
        Object.values(InterventionExternalCode), props.getMessage, "enum.InterventionExternalCode");

    this.initialSearchValues = {
      includeInactive: false,
      selfServiceOnly: false
    }

    this.state = {
      pageDataLoaded: false,
      interventionAvailabilityType: InterventionAvailabilityType.VACCINATION,

      resources: [],
      resourceMap: [],
      resourcesOptions: [],
      interventionTypeOptions,

      articleGroupOptions: [],

      foundProducts: [],
      tariffMap: [],
      productsLoaded: false,
      addEditModalOpen: false,
      specialProductsModalOpen: false,
      searchValues: this.initialSearchValues,
      cancelTokenSource: cancelTokenSource
    }

    this.fetchPageData(props);
    this.onSearchProductsSubmitClicked();
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;
    const {cancelTokenSource} = this.state;

    cancelTokenSource.cancel('Operation canceled by user');
  }

  fetchPageData = async (props?: Props) => {
    const {getMandate} = props ? props : this.props;
    const {cancelTokenSource} = this.state;

    Promise.all([
          getResources(getMandate(), cancelTokenSource),
          getAllTariffs(cancelTokenSource),
          getAllArticleGroups(cancelTokenSource),
        ])
        .then(result => {

          const resources: ResourceDto[] = result[0];
          const resourceMap = _.keyBy(result[0], "id");
          const resourcesOptions = resources.map(res => {
            return {
              key: res.id.toString(),
              text: res.description,
              value: res.id.toString()
            }
          });

          const tariffMap = _.keyBy(result[1], "id");

          const articleGroups: ArticleGroupDto[] = result[2];

          const articleGroupOptions = articleGroups.map(res => {
            return {
              key: res.id,
              text: res.description + ", " + res.externalOrderNumber + ", " + res.testLaboratoryDescription,
              value: res.id
            }
          });

          this.setState({
            resources,
            resourceMap,
            resourcesOptions: resourcesOptions,
            tariffMap,
            articleGroupOptions: articleGroupOptions,
          });

        })
        .finally(() => {
          this.setState({
            pageDataLoaded: true
          });
        });
  };


  onSaveUpsertForm = () => {
    this.onSearchProductsSubmitClicked();
    this.hideModal();
  }

  openAddEditModal = (id: string | undefined) => {
    const {cancelTokenSource} = this.state;

    if (id) {
      findProductById(id, cancelTokenSource)
          .then(response => {
            this.showModal(id, response);
          })
          .catch(noop);
    }
  };

  showModal = (id?: string, product?: UpsertProductDto) => {
    this.setState(({}: State) => {
      return {
        addEditModalOpen: true,
        product: product,
        productId: id
      };
    });
  };

  hideModal = () => {
    this.setState({
      addEditModalOpen: false
    });
  };

  showSpecialProductsModal = () => {
    this.setState(({}: State) => {
      return {
        specialProductsModalOpen: true
      }
    })
  }

  hideSpecialProductsModal = () => {
    this.setState({
      specialProductsModalOpen: false
    });
  };

  onSaveDefaultProductModal = () => {
    this.fetchPageData();
    this.hideSpecialProductsModal();
  }

  handleChange = debounce(({values}: ProductFieldProps) => {
    if (!this.mounted) {
      return;
    }

    this.setState({
          searchValues: values
        },
        () => {
          this.onSearchProductsSubmitClicked();
        });
  },300) ;

  onSearchProductsSubmitClicked = async () => {

    const {searchValues, cancelTokenSource} = this.state;
    this.setState({
      productsLoaded: false
    })

    const onFinally = () => {
      this.setState({
        productsLoaded: true
      })
    };

    searchProducts(searchValues, cancelTokenSource)
        .then(response => {
          this.setState({
            foundProducts: response
          });
        })
        .catch(noop)
        .finally(onFinally);
    this.hideModal();
  };

  deleteProduct = (id: string): void => {
    const {cancelTokenSource} = this.state;

    deleteProduct(id, cancelTokenSource)
        .then(() => {
        })
        .catch(noop);

    this.onSearchProductsSubmitClicked();
  };

  render() {
    const {pageDataLoaded, productsLoaded} = this.state;
    const {getMessage} = this.props;

    if (!pageDataLoaded) {
      return <></>;
    }

    return (
        <PraxStyledSegment id="product-box">
          <div>
            {this.renderProductSearchSegment()}
          </div>
          <StyledViewTableRow>
            {!productsLoaded ?
                <LoaderComponent message={getMessage("product.loading")}/> : this.renderProductsSegment()}
          </StyledViewTableRow>
          {this.renderUpsertProductModal()}
        </PraxStyledSegment>
    );
  };

  renderProductSearchSegment = (): JSX.Element => {

    return (
        <FinalForm
            onSubmit={this.onSearchProductsSubmitClicked}
            initialValues={initialProductsSearch}
            subscription={{values: true, pristine: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = ({handleSubmit, submitting}: FormRenderProps): JSX.Element => {
    const {getMessage} = this.props;
    const {foundProducts, interventionTypeOptions} = this.state;

    const numResults = foundProducts.length;

    return (
        <form onSubmit={handleSubmit}>
          <FormSpy subscription={{values: true}} onChange={this.handleChange}/>

          <PxGrid>
            <GridRow>
              <GridColumn width={3} verticalAlign={'middle'}>
                {getMessage("product.search.interventionType")}:
              </GridColumn>
              <GridColumn width={5}>
                <Field
                    id="externalCode"
                    name="externalCode"
                    component={Select}
                    options={interventionTypeOptions}
                    clearable
                    fluid
                />
              </GridColumn>
            </GridRow>
            <GridRow>
              <GridColumn width={3} verticalAlign={'middle'}>
                {getMessage("product.search.key")}:
              </GridColumn>
              <GridColumn width={5}>
                <Field
                    name={"id"}
                    component={Input}
                    fluid
                />
              </GridColumn>
            </GridRow>
            <GridRow>
              <GridColumn width={3} verticalAlign={'middle'}>
                {getMessage("product.search.description")}:
              </GridColumn>
              <GridColumn width={5}>
                <Field
                    name={"description"}
                    component={Input}
                    fluid
                />
              </GridColumn>
            </GridRow>
            <Grid.Row>
              <Grid.Column width={3} verticalAlign="middle">
              </Grid.Column>
              <Grid.Column width={3} verticalAlign="middle">
                <Field
                    name="includeInactive"
                    component={CheckBox}
                    label={getMessage("includingInactive")}
                    toggle={true}
                />
              </Grid.Column>
              <Grid.Column width={3} verticalAlign="middle">
                <Field
                    name="selfServiceOnly"
                    component={CheckBox}
                    label={getMessage("product.selfServiceOnly")}
                    toggle={true}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={16}>
                <AddButton
                    type="button"
                    buttonMessage={getMessage("product.specialCaseProduct")}
                    onClick={() => this.showSpecialProductsModal()}
                />
                <AddButton
                    type="button"
                    buttonMessage={getMessage("button.add")}
                    onClick={() => this.showModal()}
                />
              </Grid.Column>
            </Grid.Row>
            <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
            {this.renderSpecialProductsModal()}
          </PxGrid>
        </form>
    );
  };

  externalCodeCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;
    if (cellData) {
      return getMessage(`enum.InterventionExternalCode.${cellData}`)
    }
  };

  statusCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;
    if (cellData) {
      return getMessage(`enum.interventionBookingStatus.${cellData}`);
    }
  };

  tariffCellRenderer = ({rowData}: any) => {
    const {tariffMap} = this.state;
    const productItems = rowData.productItems;
    let tariffs: string = "";

    productItems.forEach((pi: ProductItem) => {
      if (tariffMap[pi.tariffId]) {
        tariffs += `${tariffMap[pi.tariffId].description}\n`
      } else {
        tariffs += `""\n`
      }
    })

    return tariffs;
  }

  booleanCellRenderer = ({cellData}: any) => {
    if (cellData) {
      return <Icon name="check" size="small"/>
    }
    return ""
  };

  urgencyCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    if (cellData) {
      return getMessage(`enum.urgency.${cellData}`);
    }
  };

  renderProductsSegment = (): JSX.Element => {

    const {getMessage} = this.props;
    const {foundProducts} = this.state;

    return (
        <StyledTable
            rowCount={foundProducts.length}
            rowGetter={this.productsRowGetter}
            rowRenderer={this.foundProductsRowRenderer}
            rowHeight={77}
            columns={[
              {
                width: 100,
                label: getMessage("product.tableHeader.interventionType"),
                flexGrow: 1,
                flexShrink: 0,
                dataKey: "externalCode",
                cellRenderer: this.externalCodeCellRenderer
              },
              {
                width: 50,
                label: getMessage("product.tableHeader.key"),
                dataKey: "id",
                flexGrow: 0,
                flexShrink: 0
              },
              {
                width: 225,
                flexGrow: 3,
                flexShrink: 0,
                label: getMessage("product.tableHeader.description"),
                dataKey: "description"
              },
              {
                width: 75,
                flexGrow: 0,
                flexShrink: 0,
                label: (getMessage("product.tableHeader.fromDate")),
                dataKey: "fromDate",
                cellRenderer: (this.dateCellRenderer)
              },
              {
                width: 75,
                flexGrow: 0,
                flexShrink: 0,
                label: (getMessage("product.tableHeader.toDate")),
                dataKey: "toDate",
                cellRenderer: (this.dateCellRenderer)
              },
              {
                width: 150,
                flexGrow: 1,
                flexShrink: 0,
                label: (getMessage("product.tableHeader.location")),
                dataKey: "resourceIds",
                cellRenderer: this.placeCellRenderer
              },
              {
                width: 80,
                flexGrow: 0,
                flexShrink: 0,
                label: (getMessage("product.tableHeader.tariff")),
                dataKey: "tariffId",
                cellRenderer: this.tariffCellRenderer
              },
              {
                width: 100,
                flexGrow: 0,
                flexShrink: 0,
                label: (getMessage("product.tableHeader.certificate")),
                dataKey: "generateCertificate",
                cellRenderer: this.booleanCellRenderer
              },
              {
                width: 80,
                flexGrow: 0,
                flexShrink: 0,
                label: (getMessage("product.tableHeader.report")),
                dataKey: "generateReport",
                cellRenderer: this.booleanCellRenderer
              },
              {
                width: 100,
                flexGrow: 0,
                flexShrink: 0,
                label: (getMessage("product.tableHeader.urgency")),
                dataKey: "urgency",
                cellRenderer: this.urgencyCellRenderer
              },
              {
                width: 80,
                flexGrow: 0,
                flexShrink: 0,
                label: (getMessage("product.tableHeader.selfService")),
                dataKey: "selfService",
                cellRenderer: this.booleanCellRenderer
              },
              {
                width: 100,
                flexGrow: 0,
                flexShrink: 0,
                label: (getMessage("product.tableHeader.statusAfterCheckIn")),
                dataKey: "statusAfterCheckIn",
                cellRenderer: this.statusCellRenderer
              },
              {
                width: 100,
                label: (getMessage("actions")),
                flexGrow: 0,
                flexShrink: 0,
                dataKey: "id",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}
        />
    );
  };

  productsRowGetter = ({index}: any) => {
    const {foundProducts} = this.state;

    Object.assign(foundProducts[index], {index: index + 1});

    return foundProducts[index]
  };

  foundProductsRowRenderer = ({className, columns, index, key, rowData, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {foundProducts} = this.state;

    const product = foundProducts[index];
    const productId = product.id;

    const inactive = rowData && !rowData.active;

    return (
        <div
            {...a11yProps}
            className={`${className} ${inactive ? "inactive-row" : ""}`}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(productId)}
        >
          {columns}
        </div>
    );
  };

  dateCellRenderer = ({cellData}: any) => {
    if (cellData) {
      return isoToDisplayDate(cellData);
    }
  }

  selfServiceCellRenderer = ({rowData, cellData}: any) => {
    const {getMessage} = this.props;
    const selfService = rowData.selfService;
    return selfService ? getMessage("button.yes") : getMessage("button.no");
  }

  placeCellRenderer = ({rowData, cellData}: any) => {
    const {resourceMap} = this.state;
    const resourceIds: number[] = rowData.resourceIds;

    let locations: string = "";
    resourceIds.forEach(resourceId => {
      if (resourceMap[resourceId]) {
        locations += `${resourceMap[resourceId].description}\n`
      }
    });

    return locations;
  }

  actionsCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => this.openAddEditModal(cellData)}
          />
          <DeleteActionButton
              hoverMessage={getMessage('msgDelete')}
              popupMessage={getMessage("product.confirmDelete")}
              onConfirm={() => this.deleteProduct(cellData)}
          />
        </React.Fragment>
    );
  };

  renderUpsertProductModal = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {resourcesOptions, articleGroupOptions, product, addEditModalOpen} = this.state;

    const productId = this.state.productId;

    return (
        <Modal
            open={addEditModalOpen}
            onClose={() => this.hideModal()}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("product.form.title")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertProductForm
                onCancel={this.hideModal}
                onSave={this.onSaveUpsertForm}
                productId={productId}
                product={product}
                resourceOptions={resourcesOptions}
                articleGroupOptions={articleGroupOptions}
            />
          </FormModalContentContainer>
        </Modal>
    );
  };

  renderSpecialProductsModal = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {specialProductsModalOpen, foundProducts} = this.state;

    return (
        <Modal
            open={specialProductsModalOpen}
            onClose={() => this.hideSpecialProductsModal()}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("product.specialCaseProduct")}</Modal.Header>
          <FormModalContentContainer>
            <SpecialCaseProductModal
                onCancel={this.hideSpecialProductsModal}
                onSave={this.onSaveDefaultProductModal}
                foundProducts={foundProducts}
            />
          </FormModalContentContainer>
        </Modal>
    );
  };

}

export default withMessages(withAuthContext(withRouterWorkaround(ProductView)));
